import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { ClientsKpiTiles, type ClientsKpiTile } from '@/components/clients/kpi-tiles';
import {
  ClientsTable,
  type ClientsTableRow,
  type ClientsTableEngagement,
} from '@/components/clients/table';
import { type TeamMember, deriveInitials } from '@/components/clients/team-avatars';
import { CreateClientModal } from '@/components/modals/directory-create-modals';
import { useClientsDetailedQuery, useClientsQuery, type ClientDetail } from '@/lib/api/clients';
import { useEngagementsListQuery } from '@/lib/api/engagements';
import { usePortfolioQuery } from '@/lib/api/portfolio';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { rollupColorToPostureStatus } from '@/lib/api/health-posture';
import { cn } from '@/lib/utils/cn';

/**
 * Clients table screen (T-0108 / E01-F01 / C20-130).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/screens-gaps.js → Screens
 * .clients`. C20-130 tightens the prior implementation to match the
 * prototype's tile labels (Total clients · Active engagements · Avg
 * engagements/client · Sectors served — the prototype contract, not the
 * T-0127 KPI rollup set) and adds the missing table columns (primary
 * contact, multi-line active-engagements list, onboarded date, status).
 *
 * Data sources — every visible element binds to a real backend read; no
 * mock data anywhere:
 *   - `GET /api/v1/clients`              → list, name, type, status
 *   - `GET /api/v1/clients/:id`          → primary contact, team_member_user_ids,
 *                                          created_at, updated_at (per-row fan-out)
 *   - `GET /api/v1/engagements`          → engagement count + planned_end per client
 *   - `GET /api/v1/portfolio`            → per-engagement posture + name (for the
 *                                          active-engagements column)
 *   - `GET /api/v1/users/lookup`         → display names + avatar tones
 *   - `GET /api/v1/admin/firm/kpi-rollups` → reserved for an optional second-row
 *                                            KPI strip; the prototype's primary
 *                                            tiles derive from the lists above.
 *
 * The "New client" CTA carries `onClick={() => setCreatingClient(true)}`. T-0117 owns the
 * generic create modal that wires this trigger to the actual form.
 */
export const Route = createFileRoute('/clients/')({
  component: ClientsPage,
});

function ClientsPage() {
  return (
    <RequireAuth>
      <div className="space-y-6 p-card-pad">
        <ClientsContent />
      </div>
    </RequireAuth>
  );
}

type SectorTab = 'all' | 'city' | 'county' | 'state_agency' | 'other';

function ClientsContent() {
  const clients = useClientsQuery();
  const clientIds = React.useMemo(() => (clients.data ?? []).map((c) => c.id), [clients.data]);
  // Fan-out detail per client — supplies primary contact, team_member_user_ids,
  // created_at, updated_at. Each query is de-duplicated with the detail-page
  // query so visiting the page after browsing rows doesn't refetch.
  const details = useClientsDetailedQuery(clientIds);
  // Caller-scoped firm-wide engagements list — used to compute engagement_count
  // per client and to surface the active-engagements column.
  const engagements = useEngagementsListQuery();
  // Composite portfolio read — used solely for posture per engagement (we
  // don't render its other fields on this screen).
  const portfolio = usePortfolioQuery();

  const [activeSector, setActiveSector] = React.useState<SectorTab>('all');
  const [creatingClient, setCreatingClient] = React.useState(false);
  const [query, setQuery] = React.useState('');

  // Resolve all team-member ids in one batched lookup. These hooks MUST run
  // on every render — they previously sat below the loading/error early
  // returns, so the first render ran fewer hooks than the second and React
  // threw "rendered more hooks than during the previous render" as soon as
  // the clients query resolved after a paint.
  const allTeamIds = React.useMemo(() => {
    const set = new Set<string>();
    for (const q of details) {
      for (const uid of q.data?.team_member_user_ids ?? []) set.add(uid);
    }
    return Array.from(set);
  }, [details]);
  const userLookup = useUsersLookupQuery(allTeamIds);
  const lookupById = React.useMemo(() => {
    const m = new Map<string, { display_name: string; avatar_color_index: number }>();
    for (const item of userLookup.data?.items ?? []) {
      m.set(item.id, {
        display_name: item.display_name,
        avatar_color_index: item.avatar_color_index,
      });
    }
    return m;
  }, [userLookup.data]);

  if (clients.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading clients…
      </div>
    );
  }
  if (clients.error) return <ErrorMessage error={clients.error} />;

  const summaries = clients.data ?? [];
  const detailById = new Map<string, ClientDetail | undefined>();
  details.forEach((q, idx) => {
    const id = clientIds[idx];
    if (id) detailById.set(id, q.data);
  });

  const engagementsByClientId = new Map<
    string,
    NonNullable<typeof engagements.data>['engagements']
  >();
  if (engagements.data) {
    for (const e of engagements.data.engagements) {
      const list = engagementsByClientId.get(e.client_id) ?? [];
      list.push(e);
      engagementsByClientId.set(e.client_id, list);
    }
  }

  // Posture lookup: engagement_id → posture string from composite portfolio.
  const postureByEngId = new Map<string, string>();
  for (const e of portfolio.data?.engagements ?? []) {
    postureByEngId.set(e.id, e.posture);
  }

  const rowsAll: ClientsTableRow[] = summaries.map((c) => {
    const detail = detailById.get(c.id);
    const teamIds: ReadonlyArray<string> = detail?.team_member_user_ids ?? [];
    const team: TeamMember[] = teamIds.map((uid) => {
      const resolved = lookupById.get(uid);
      if (resolved) {
        return {
          id: uid,
          name: resolved.display_name,
          initials: deriveInitials(resolved.display_name),
        };
      }
      const fragment = uid.slice(0, 8);
      return {
        id: uid,
        name: fragment,
        initials:
          deriveInitials(fragment.replace(/[-_]/g, ' ').slice(0, 5)) ||
          uid.slice(0, 2).toUpperCase(),
      };
    });
    const engs = engagementsByClientId.get(c.id) ?? [];
    const engagementsList: ClientsTableEngagement[] = engs.map((e) => ({
      id: e.id,
      name: e.project_name ?? e.name,
      posture: rollupColorToPostureStatus(
        (postureByEngId.get(e.id) as
          | 'green'
          | 'yellow_divergent'
          | 'red'
          | 'grey'
          | 'unassessed'
          | undefined) ?? null,
      ),
      phase: undefined,
    }));
    return {
      id: c.id,
      name: c.name,
      client_type: c.client_type,
      status: c.status,
      primary_contact_name: detail?.primary_contact_name,
      primary_contact_email: detail?.primary_contact_email,
      engagements: engagementsList,
      team,
      onboarded: detail?.created_at,
      last_activity: detail?.updated_at,
    };
  });

  // Apply sector tab + search filters.
  const rows = rowsAll.filter((row) => {
    if (activeSector !== 'all' && row.client_type !== activeSector) return false;
    if (query.trim().length > 0) {
      const needle = query.trim().toLowerCase();
      if (!row.name.toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  // Sector counts — drive the tab strip.
  const sectorCounts = {
    all: rowsAll.length,
    city: rowsAll.filter((r) => r.client_type === 'city').length,
    county: rowsAll.filter((r) => r.client_type === 'county').length,
    state_agency: rowsAll.filter((r) => r.client_type === 'state_agency').length,
    other: rowsAll.filter((r) => !['city', 'county', 'state_agency'].includes(r.client_type))
      .length,
  };

  // KPI tiles — per prototype Screens.clients:
  //   Total clients · Active engagements · Avg engagements/client · Sectors served
  const totalClients = rowsAll.length;
  const activeClients = rowsAll.filter((r) => r.status.toLowerCase() === 'active').length;
  const totalEngagements = rowsAll.reduce((a, r) => a + r.engagements.length, 0);
  const orphanCount = rowsAll.filter((r) => r.engagements.length === 0).length;
  const sectorsServed = new Set(rowsAll.map((r) => r.client_type)).size;
  const sampleSectors = Array.from(new Set(rowsAll.map((r) => r.client_type))).slice(0, 3);

  const tiles: ClientsKpiTile[] = [
    {
      label: 'Total clients',
      value: totalClients,
      trend: `${activeClients} active · ${Math.max(0, totalClients - activeClients)} other`,
      source: 'Clients',
    },
    {
      label: 'Active engagements',
      value: totalEngagements,
      trend: `across ${activeClients || totalClients} client${activeClients === 1 ? '' : 's'}`,
      source: 'Engagements',
    },
    {
      label: 'Avg engagements / client',
      value:
        activeClients > 0
          ? (totalEngagements / activeClients).toFixed(1)
          : totalClients > 0
            ? (totalEngagements / totalClients).toFixed(1)
            : '—',
      trend:
        orphanCount === 0
          ? 'no clients without a live project'
          : `${orphanCount} client${orphanCount === 1 ? '' : 's'} without a live project`,
      source: 'Engagements per client',
    },
    {
      label: 'Sectors served',
      value: sectorsServed,
      trend: sampleSectors.map(humanizeSector).join(' · '),
      source: 'Client types',
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Clients</h1>
          <p className="mt-1 text-meta text-ink-500">
            {totalClients} client{totalClients === 1 ? '' : 's'} · {activeClients} active ·{' '}
            {totalEngagements} project{totalEngagements === 1 ? '' : 's'} across portfolio
            {orphanCount > 0 ? (
              <>
                {' '}
                ·{' '}
                <span style={{ color: 'var(--accent-amber-700)' }}>
                  {orphanCount} without a project
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="inline-flex h-8 items-center gap-2 rounded-chip border border-ink-200 bg-paper-50 px-2.5"
            aria-label="Search clients"
          >
            <Search className="h-3.5 w-3.5 text-ink-500" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              className="w-[180px] border-0 bg-transparent text-body-sm text-ink-900 placeholder:text-ink-300 focus:outline-none"
            />
          </label>
          <Button
            type="button"
            onClick={() => setCreatingClient(true)}
            size="sm"
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New client
          </Button>
        </div>
      </div>

      <ClientsKpiTiles tiles={tiles} />

      <section className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-1">
            <SectorTabButton active={activeSector === 'all'} onClick={() => setActiveSector('all')}>
              All <span className="font-mono text-ink-500">· {sectorCounts.all}</span>
            </SectorTabButton>
            <SectorTabButton
              active={activeSector === 'city'}
              onClick={() => setActiveSector('city')}
            >
              Cities <span className="font-mono text-ink-500">· {sectorCounts.city}</span>
            </SectorTabButton>
            <SectorTabButton
              active={activeSector === 'county'}
              onClick={() => setActiveSector('county')}
            >
              Counties <span className="font-mono text-ink-500">· {sectorCounts.county}</span>
            </SectorTabButton>
            <SectorTabButton
              active={activeSector === 'state_agency'}
              onClick={() => setActiveSector('state_agency')}
            >
              State agencies{' '}
              <span className="font-mono text-ink-500">· {sectorCounts.state_agency}</span>
            </SectorTabButton>
            <SectorTabButton
              active={activeSector === 'other'}
              onClick={() => setActiveSector('other')}
            >
              Other <span className="font-mono text-ink-500">· {sectorCounts.other}</span>
            </SectorTabButton>
          </div>
          <span className="text-meta text-ink-500">Click a project to jump to its overview</span>
        </div>
        <ClientsTable rows={rows} />
      </section>

      <CreateClientModal open={creatingClient} onOpenChange={setCreatingClient} />
    </>
  );
}

function SectorTabButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-chip px-2.5 text-body-sm font-medium transition-colors',
        active
          ? 'bg-paper-100 text-ink-900'
          : 'bg-transparent text-ink-700 hover:bg-paper-50 hover:text-ink-900',
      )}
    >
      {children}
    </button>
  );
}

function humanizeSector(sector: string): string {
  switch (sector) {
    case 'state_agency':
      return 'State agency';
    case 'county':
      return 'County';
    case 'city':
      return 'City';
    default:
      return sector.charAt(0).toUpperCase() + sector.slice(1);
  }
}
